import NumberPrecision from 'number-precision';
import HallModel from './HallModel';
import TableModel from './TableModel';
import BettingModel from './BettingModel';
import Model from '../../../../../common/scripts/Toolkit/Helper/Model';
import TableGame from '../../../System/TableGame';
import { PathName } from '../Config/PathConfig';

enum UserEventType {
    ACTUAL_BALANCE_RECEIVED = 'ACTUAL_BALANCE_RECEIVED',
    BALANCE_CHANGED = 'BALANCE_CHANGED',
    CAN_EXIT_STATE_CHANGED = 'CAN_EXIT_STATE_CHANGED',
    CREDIT_LIMIT_TRIGGER_STATE_CHANGED = 'CREDIT_LIMIT_TRIGGER_STATE_CHANGED',
    KEY_OUT_VALUE_CHANGED = 'KEY_OUT_VALUE_CHANGED',
    FAVORITE_TABLE_LIST_UPDATED = 'FAVORITE_TABLE_LIST_UPDATED',
    TRANSACTION_RECEIVED = 'TRANSACTION_RECEIVED',
}

export default class UserModel extends Model {
    public static readonly EVENT_TYPE = UserEventType;

    protected _hallModel: HallModel = null;

    protected _nickname = '';
    protected _avatar = '';
    protected _balance = 0;
    protected _actualBalance = 0;
    protected _isTrial = false;
    protected _canExit = true;
    protected _holdingWinValue = 0;
    protected _holdingBetValue = 0;
    protected _currency: string = '';
    protected _currencyRatio: number = 1;
    protected _lastTable: string = '';
    protected _transactionID: string = '';

    public get gameCredit() {
        return this._balance;
    }

    public get balance() {
        return this._balance;
    }

    public get isTrial() {
        return this._isTrial;
    }

    protected set actualBalance(value: number) {
        this.emit(UserModel.EVENT_TYPE.ACTUAL_BALANCE_RECEIVED);
        if (this._actualBalance === value) {
            return;
        }
        this._actualBalance = value;
        this.updateDisplayBalance();
    }

    public get nickname() {
        return this._nickname;
    }

    public get avatar() {
        return this._avatar;
    }

    public get canExit() {
        return this._canExit;
    }
    protected set canExit(flag: boolean) {
        if (this._canExit === flag) {
            return;
        }
        this._canExit = flag;
        this.emit(UserModel.EVENT_TYPE.CAN_EXIT_STATE_CHANGED);
    }

    public get currency() {
        return this._currency;
    }

    public get currencyRatio() {
        return this._currencyRatio;
    }

    public get lastTable() {
        return this._lastTable;
    }

    public get transactionID() {
        return this._transactionID;
    }

    protected set transactionID(value: string) {
        if (!value || this._transactionID === value) {
            return;
        }
        this._transactionID = value;
        this.emit(UserModel.EVENT_TYPE.TRANSACTION_RECEIVED);
    }

    public transformToCurrency(amount: number) {
        const amountInDollar = TableGame.account.valueToCredit(amount);
        return +NumberPrecision.times(amountInDollar, TableGame.user.currencyRatio || 1).toFixed(2);
    }

    public init() {
        this._hallModel = TableGame.hall;
        TableGame.connection.on(PathName.Balance, (data) => {
            this.actualBalance = TableGame.currentTable?.isTrial ? data.TrialBalance : data.Balance;
        });
        TableGame.connection.on(PathName.Bet, (data) => {
            // HACK
            // this.actualBalance = TableGame.currentTable?.isTrial ? data.TrialBalance : data.Balance;
            this.actualBalance = data.Balance;
            this.transactionID = data.TransactionID || '';
        });
        TableGame.connection.on(PathName.PlayerSettle, (data) => {
            this.actualBalance = TableGame.currentTable?.isTrial ? data.TrialBalance : data.Balance;
        });
        TableGame.connection.on(PathName.ExitTable, (data) => {
            this.actualBalance = data.Balance;
        });

        this._hallModel.on(HallModel.EVENT_TYPE.TABLE_LIST_RECEIVED, (newTableIDList?: number[]) => {
            newTableIDList?.forEach((newTableID) => {
                const tableModel = this._hallModel.getTableByID(newTableID);
                tableModel.on(TableModel.EVENT_TYPE.HOLDING_WIN_STATE_CHANGED, this.updateHoldingWinValue, this);
                const { bettingModel } = tableModel;
                bettingModel.on(BettingModel.EVENT_TYPE.HOLDING_BET_STATE_CHANGED, this.updateHoldingBetValue, this);
                bettingModel.on(BettingModel.EVENT_TYPE.USER_BET_CHANGED, this.onUserBetChanged, this);
                this.updateHoldingWinValue();
                this.onUserBetChanged();
            });
        });
    }

    public async login(): Promise<void> {
        const { token, platformID, walletType } = TableGame.config;
        const { Nickname, Avatar, Balance, AccountCurrency, CurrencyRatio, AccountInfoURL, LastTable } = await TableGame.connection.send(PathName.Login, {
            Token: token,
            PlatformsID: platformID,
            WalletTypesID: walletType,
        });
        this._nickname = Nickname;
        this._avatar = Avatar;
        this._isTrial = !AccountInfoURL;
        this.actualBalance = Balance;
        this._currency = AccountCurrency;
        this._lastTable = LastTable;
        this._currencyRatio = CurrencyRatio || 1;
    }

    protected onUserBetChanged() {
        this.updateHoldingBetValue();
        this.updateCanExitState();
    }

    protected updateHoldingWinValue() {
        this._holdingWinValue = this._hallModel.tableModelList
            .map((tableModel) => (tableModel.holdingWin ? tableModel.result.totalWin : 0))
            .reduce((pre, cur) => pre + cur, 0);
        this.updateDisplayBalance();
    }

    protected updateHoldingBetValue() {
        this._holdingBetValue = this._hallModel.tableModelList
            .map(({ bettingModel }) => (bettingModel.holdingBet ? bettingModel.userBet : 0))
            .reduce((pre, cur) => pre + cur, 0);
        this.updateDisplayBalance();
    }

    protected updateCanExitState() {
        this.canExit = !this._hallModel.tableModelList.some(({ bettingModel }) => bettingModel.userBet);
    }

    protected updateDisplayBalance() {
        const newBalance = this._actualBalance - this._holdingBetValue - this._holdingWinValue;
        if (this._balance === newBalance) {
            return;
        }
        this._balance = newBalance;
        this.emit(UserModel.EVENT_TYPE.BALANCE_CHANGED);
    }
}
